import { apolloClient } from "@/lib/graphql";
import { graphql } from "@/__generated__";

declare global {
  interface Window {
    OneSignal?: any;
    OneSignalDeferred?: Array<(os: any) => void | Promise<void>>;
  }
}

const REGISTER_PUSH_SUBSCRIPTION = graphql(`
  mutation RegisterPushSubscription($input: RegisterPushSubscriptionInput!) {
    registerPushSubscription(input: $input) {
      success
      errors
    }
  }
`);

let scriptInjected = false;

function injectScript() {
  if (scriptInjected) return;
  if (typeof document === "undefined") return;
  scriptInjected = true;
  const s = document.createElement("script");
  s.src = "https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js";
  s.defer = true;
  document.head.appendChild(s);
}

let oneSignalInitialized = false;

// Gated behind the marketing consent by applyConsentToSdks (#171) — never called
// before consent. Idempotent so the consent-boot path and a later grant are safe.
export function initOneSignal(appId: string | undefined) {
  if (!appId) return;
  if (typeof window === "undefined") return;
  if (oneSignalInitialized) return;
  oneSignalInitialized = true;

  injectScript();
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(async (OneSignal) => {
    await OneSignal.init({
      appId,
      serviceWorkerParam: { scope: "/" },
      serviceWorkerPath: "/OneSignalSDKWorker.js",
      allowLocalhostAsSecureOrigin: true,
    });

    OneSignal.User.PushSubscription.addEventListener("change", async (evt: { current: { id?: string } }) => {
      const playerId = evt?.current?.id;
      if (!playerId) return;
      try {
        await apolloClient.mutate({
          mutation: REGISTER_PUSH_SUBSCRIPTION,
          variables: { input: { onesignalPlayerId: playerId, platform: "web" } },
        });
      } catch (e) {
        console.warn("[onesignal] failed to register player_id with backend", e);
      }
    });
  });
}

// Whether asking is worth offering at all. Deliberately SYNCHRONOUS and
// SDK-free: `oneSignalInitialized` above is set at the start of init, before
// OneSignal has actually loaded, so it means "we started", not "it works" —
// gating a rendered offer on it would either hide the offer for good on a slow
// load or show one whose button spins forever behind an ad blocker.
//
// "denied" is a dead end: a browser will not re-prompt after a hard refusal and
// nothing in JavaScript can undo it, so there is nothing honest to offer.
export function canOfferPush(): boolean {
  if (typeof window === "undefined" || !("Notification" in window)) return false;
  return window.Notification.permission !== "denied";
}

export type PushPermissionOutcome = "granted" | "denied" | "dismissed" | "unavailable";

// How long to wait for the SDK before giving up. The student is looking at a
// spinner, and a prompt that never arrives must resolve as "dismissed" — never
// hang, and never be reported as success.
const PUSH_PROMPT_TIMEOUT_MS = 10_000;

// Asks the browser, and reports what actually happened. The caller writes the
// preference only on "granted": a preference that is on with no device
// registered is exactly the silent lie this feature exists to avoid.
export async function requestPushPermission(): Promise<PushPermissionOutcome> {
  if (typeof window === "undefined" || !("Notification" in window)) return "unavailable";
  if (window.Notification.permission === "granted") return "granted";
  if (window.Notification.permission === "denied") return "denied";

  const asked = new Promise<PushPermissionOutcome>((resolve) => {
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        await OneSignal.Notifications.requestPermission();
        resolve(window.Notification.permission === "granted" ? "granted" : "dismissed");
      } catch {
        resolve("dismissed");
      }
    });
  });
  const timeout = new Promise<PushPermissionOutcome>((resolve) =>
    window.setTimeout(() => resolve("dismissed"), PUSH_PROMPT_TIMEOUT_MS),
  );
  return Promise.race([asked, timeout]);
}

// Best effort: the player id normally reaches the server through the "change"
// listener in initOneSignal, but that listener can miss one (it is attached
// after init, and a subscription that already existed fires nothing). A promise
// of a daily reminder is only deliverable if the row exists, so ask once more
// straight after a grant. Failure is silent by design — the listener remains
// the primary path and the student has already been told they are subscribed.
export async function syncPushSubscription(): Promise<void> {
  if (typeof window === "undefined") return;
  await new Promise<void>((resolve) => {
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        const playerId = OneSignal?.User?.PushSubscription?.id;
        if (playerId) {
          await apolloClient.mutate({
            mutation: REGISTER_PUSH_SUBSCRIPTION,
            variables: { input: { onesignalPlayerId: playerId, platform: "web" } },
          });
        }
      } catch (e) {
        console.warn("[onesignal] could not sync the push subscription after a grant", e);
      }
      resolve();
    });
  });
}

// The push subscription of THIS browser, if OneSignal has loaded and the person
// subscribed. Read synchronously and never awaited: signing out must not wait on
// a third-party script, and without an id the server simply keeps the rows (#609).
export function currentPushPlayerId(): string | null {
  if (typeof window === "undefined") return null;
  return window.OneSignal?.User?.PushSubscription?.id ?? null;
}

export function showSoftPrompt() {
  if (typeof window === "undefined") return;
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(async (OneSignal) => {
    await OneSignal.Slidedown.promptPush();
  });
}
